import React, { useMemo } from 'react';

import { usePostMessageContext, usePostMessageHandler, useConfig } from 'src/providers';
import { useAnalytics } from './AnalyticsProvider';
import { useFeatures } from './FeatureProvider';
import { useLocale } from './LocaleProvider';

import { CtaEvent, DeclineSourceType, ModalType, SelectContractEvent } from 'src/types/Events';
import { IContract } from 'src/types/IContract';

export interface IContractWithAttributes {
  id: string;
  type: string;
  attributes: IContract & {
    sku: string;
    recommendedPrice: number;
    bulletPoints: string;
  };
}

type SetActiveProductEventDetailType = {
  activeProduct: string;
  price: number;
  contracts: (IContract & { contractId: string })[];
} | {
  name: string;
  sku: string;
  price: string,
  contracts: IContractWithAttributes[],
};

interface IContractContext {
  addProtection: (modalType?: ModalType) => void;
  declineProtection: (source: DeclineSourceType, modalType: ModalType) => void;
  productName: string;
  productPrice: number;
  contracts: IContract[];
  startingAtPrice: number;
  deselectContract: (contractIdOrSku: string) => void;
  protectionAdded: boolean;
  resolved: boolean;
  selectContract: (contractIdOrSku: string, initiatedByUser?: boolean) => void;
  selectedContract: IContract | null;
  selectedContractId: string | null;
  selectDecline: () => void;
  toggleContract: (contractId: string) => void;
  ipidUrl: string | null;
}

const ContractContext = React.createContext<IContractContext | null>(null);

export const ContractProvider = ({ children }: { children: React.ReactNode }) => {
  const { track } = useAnalytics();
  const { postMessage, source } = usePostMessageContext();
  const config = useConfig();
  const { cartType } = useFeatures();
  const { setCurrency } = useLocale();
  const [ ipidUrl , setIpidUrl ] = React.useState<IContractContext['ipidUrl']>(null);
  const [contracts, setContracts] = React.useState<IContract[]>([]);
  const [productPrice, setProductPrice] = React.useState(0);
  const [productName, setProductName] = React.useState('');
  const [selectedContractId, setSelectedContractId] = React.useState<string | null>(null);
  const [resolved, setResolved] = React.useState(false);
  const [protectionAdded, setProtectionAdded] = React.useState(false);
  const isSkuMode = React.useRef<boolean>(false);
  const isDeclineOptionEnabled = React.useRef<boolean>(false);

  const startingAtPrice = useMemo(() => {
    if (contracts.length) {
      return parseFloat(contracts[0].price);
    }
    return 0;
  }, [contracts]);

  React.useEffect(() => {
    if (config.type === 'radio' && config.showCtaPdpDeclineOption) {
      isDeclineOptionEnabled.current = false;
    }
  }, [config.type, config.showCtaPdpDeclineOption]);

  // Toggle resolved state back to false to send a reset signal
  React.useEffect(() => {
    if (resolved) {
      setResolved(false);
    }
  }, [resolved, setResolved]);

  const deselectContract = React.useCallback(() => {
    setSelectedContractId(null);
    postMessage('deselectContract', {});
  }, [setSelectedContractId, postMessage]);

  const ctaType = useMemo(() => {
    switch (source) {
      case 'cart':
      case 'modal':
        return source;
      default:
      case 'product':
        return 'productPage';
    }
  }, [source]);

  const selectedContract = useMemo(() => (
    contracts.find(({ id }) => id === selectedContractId) || null
  ), [contracts, selectedContractId]);

  const buildCtaEvent = React.useCallback((modalType?: ModalType): CtaEvent => {
    switch (ctaType) {
      case 'cart':
        return {
          ctaType: 'cart',
          cartType,
        };
      default:
      case 'productPage':
        return {
          ctaType: 'productPage',
          productPageType: config.type,
        };
      case 'modal':
        return {
          ctaType: 'modal',
          modalType: modalType || 'response',
        };
    }
  }, [ctaType, config.type, cartType]);

  const buildSelectContractEvent = React.useCallback((
    id: string | null,
    modalType?: ModalType,
  ): SelectContractEvent => {
    // NOTE: can't use memoized selectedContract because it won't be updated yet
    const termSelected = contracts.find(contract => contract.id === id)?.term;
    return {
      ...buildCtaEvent(modalType),
      ...(isSkuMode.current ? { sku: id } : { contractId: id }),
      termSelected,
    };
  }, [contracts]);

  const addProtection = React.useCallback((modalType?: ModalType) => {
    postMessage('addProtection', buildSelectContractEvent(selectedContractId, modalType));
    if (modalType) {
      // NOTE: this event is only fired from modal
      track('add_protection', {
        ctaVersion: '1.0.0',
        type: 'modal',
        modalType,
        cartType: config.cartType,
        selectedContractSku: selectedContract?.id,
        termSelected: selectedContract?.term,
      });
    }
    setResolved(true);
  }, [postMessage, buildSelectContractEvent, selectedContractId, track, config.cartType]);

  const declineProtection = React.useCallback((source: DeclineSourceType, modalType: ModalType) => {
    // NOTE: this interface is different for widget vs OOTB!
    if (isSkuMode.current) {
      postMessage('decline', {
        activeContract: {
          sku: selectedContractId,
        },
        declineSource: source,
        modalType,
      });
    } else {
      postMessage('decline', {
        contractId: selectedContractId,
        declineSource: source,
        modalType,
      });
    }
    if (isDeclineOptionEnabled.current) {
      selectDecline();
    }
    setResolved(true);
  }, [postMessage, selectedContractId]);

  const selectContract = React.useCallback((id: string, initiatedByUser = true) => {
    setSelectedContractId(id);
    // TODO: don't fire analytics event when initiatedByUser=false
    if (initiatedByUser) {
      postMessage('selectContract', buildSelectContractEvent(id));
    }
  }, [setSelectedContractId, postMessage, buildSelectContractEvent]);

  const selectDecline = React.useCallback(() => {
    postMessage('selectDecline', buildSelectContractEvent('decline'));
  }, [postMessage, buildSelectContractEvent]);

  const toggleContract = React.useCallback((contractId: string) => {
    if (selectedContractId === contractId) {
      deselectContract();
    } else {
      selectContract(contractId);
    }
  }, [selectedContractId, selectContract, deselectContract]);

  usePostMessageHandler('setActiveProduct', React.useCallback((e: Event) => {
    const { detail } = (e as CustomEvent<SetActiveProductEventDetailType>);

    // Handle the interface differences between widget and OOTB scripts
    isSkuMode.current = ('sku' in detail);

    // Normalize contracts to always have an id property
    const contracts = (detail.contracts || []).map((contract) => {
      if ('attributes' in contract) {
        return {
          ...contract.attributes,
          bulletPoints: contract.attributes.bulletPoints.split('|'),
          id: contract.attributes.sku,
          price: contract.attributes.recommendedPrice.toFixed(2),
        };
      }
      return {
        ...contract,
        id: contract.contractId,
      };
    });

    // Infer the currency from the first contract
    if (contracts[0]?.currency) {
      setCurrency(contracts[0].currency);
    }

    if (contracts[0]?.ipidUrl) {
      setIpidUrl(contracts[0].ipidUrl);
    }

    setContracts(contracts);

    // Optional: set product name
    // NOTE: currently only used by widget shops (Bugaboo, specifically)
    if ('name' in detail) {
      if (typeof detail.name === 'string' && detail.name.trim() !== '') {
        setProductName(detail.name.trim()); // sets name only when it exists and is a string
      } else {
        setProductName(''); // resets name when it's undefined
      }
    }

    // Optional: set product price
    // NOTE: Widget only. OOTB scripts set `productPrice` on the contract instead
    if ('price' in detail && detail.price) {
      setProductPrice(Number(detail.price));
    }
  }, [setContracts, setProductPrice, setProductName]));

  // Keep product price in sync with selected contract
  React.useEffect(() => {
    if (selectedContract?.productPrice) {
      setProductPrice(Number(selectedContract?.productPrice));
    }
  }, [selectedContract?.productPrice]);

  usePostMessageHandler('selectContract', React.useCallback((e: Event) => {
    // eslint-disable-next-line
    const ev = e as CustomEvent<any>;
    if (ev.detail.sku) {
      setSelectedContractId(ev.detail.sku);
    } else {
      setSelectedContractId(ev.detail.contractId);
    }
  }, [setSelectedContractId]));

  usePostMessageHandler('deselectContract', React.useCallback(() => {
    setSelectedContractId(null);
  }, [setSelectedContractId]));

  usePostMessageHandler('selectDecline', React.useCallback(() => {
    setSelectedContractId('decline');
  }, [setSelectedContractId]));

  usePostMessageHandler('addProtection', React.useCallback(() => {
    setProtectionAdded(true);
  }, [setProtectionAdded]));

  usePostMessageHandler('decline', React.useCallback(() => {
    setProtectionAdded(false);
  }, [setProtectionAdded]));

  const value: IContractContext = {
    addProtection,
    contracts,
    startingAtPrice,
    declineProtection,
    deselectContract,
    protectionAdded,
    productName,
    productPrice,
    resolved,
    selectContract,
    selectedContract,
    selectedContractId,
    selectDecline,
    toggleContract,
    ipidUrl,
  };

  // IMPORTANT: don't ever short-circuit here if there are no contracts,
  // because a child element is responsible for emitting an "initialized" event,
  // which is how we get contracts from the parent frame.

  return (
    <ContractContext.Provider value={value}>
      {children}
    </ContractContext.Provider>
  );
};

export const useContractContext = () => {
  const context = React.useContext(ContractContext);
  if (!context) {
    throw new Error('Context misconfigured: ContractContext');
  }
  return context;
};
